import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Spinner } from '@opendate-app/setlist';

const axios = require('axios').default;

import { toastError, toastSuccess } from '../../../shared/toastHelper';
import MembershipProductCardView, { membershipBenefitSummary } from './MembershipProductCardView';

const mutedTextStyle = { color: 'var(--sl-text-tertiary)' };

const MembershipProductCard = ({
  product,
  team,
  ticketTypes,
  addOns,
  revealsBasePath,
  promoCodePath,
  onToggle,
  onPriceSave,
  onMonthlyPriceSave,
  onOrganizerFeesWaivedChange,
  onProductsChange,
  membershipProductId,
}) => {
  const membershipProduct = product.membership_product;
  const checked = product.checked;
  const promoCode = product.membership_promo_code;
  const promoEnabled = !!promoCode;

  const hasAnnualPricing = product.effective_price_cents != null;
  const hasMonthlyPricing = product.effective_monthly_price_cents != null;
  const initialPrice = product.effective_price_cents ? (product.effective_price_cents / 100.0).toFixed(2) : '';
  const initialMonthlyPrice = product.effective_monthly_price_cents ? (product.effective_monthly_price_cents / 100.0).toFixed(2) : '';
  const [priceDisplay, setPriceDisplay] = useState(initialPrice);
  const [monthlyPriceDisplay, setMonthlyPriceDisplay] = useState(initialMonthlyPrice);
  const priceDebounceTimer = useRef(null);
  const monthlyPriceDebounceTimer = useRef(null);

  const [percentageValue, setPercentageValue] = useState(promoCode?.percentage ?? 0);
  const [amountValue, setAmountValue] = useState(promoCode?.amount_two_decimals || promoCode?.amount || '');
  const [limitPerMemberValue, setLimitPerMemberValue] = useState(promoCode?.ticket_limit_per_member ?? 0);
  const promoDebounceTimer = useRef(null);
  const pendingRevealRequests = useRef(new Set());

  const withPendingReveal = (key, request) => {
    if (pendingRevealRequests.current.has(key)) return;

    pendingRevealRequests.current.add(key);
    request().finally(() => pendingRevealRequests.current.delete(key));
  };

  useEffect(() => {
    setPriceDisplay(product.effective_price_cents ? (product.effective_price_cents / 100.0).toFixed(2) : '');
  }, [product.effective_price_cents]);

  useEffect(() => {
    setMonthlyPriceDisplay(product.effective_monthly_price_cents ? (product.effective_monthly_price_cents / 100.0).toFixed(2) : '');
  }, [product.effective_monthly_price_cents]);

  useEffect(() => {
    if (promoCode) {
      setPercentageValue(promoCode.percentage ?? 0);
      setAmountValue(promoCode.amount_two_decimals || promoCode.amount || '');
      setLimitPerMemberValue(promoCode.ticket_limit_per_member ?? 0);
    }
  }, [promoCode?.id]);

  useEffect(() => () => {
    clearTimeout(priceDebounceTimer.current);
    clearTimeout(monthlyPriceDebounceTimer.current);
    clearTimeout(promoDebounceTimer.current);
  }, []);

  const debouncedPriceSave = useCallback((value) => {
    clearTimeout(priceDebounceTimer.current);
    priceDebounceTimer.current = setTimeout(() => {
      const newCents = Math.round((parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0) * 100);
      if (newCents <= 0) return;
      if (newCents !== (product.effective_price_cents || 0)) {
        onPriceSave(newCents);
      }
    }, 800);
  }, [product.effective_price_cents, onPriceSave]);

  const handlePriceChange = (e) => {
    const value = e.target.value;
    setPriceDisplay(value);
    debouncedPriceSave(value);
  };

  const debouncedMonthlyPriceSave = useCallback((value) => {
    clearTimeout(monthlyPriceDebounceTimer.current);
    monthlyPriceDebounceTimer.current = setTimeout(() => {
      const newCents = Math.round((parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0) * 100);
      if (newCents <= 0) return;
      if (newCents !== (product.effective_monthly_price_cents || 0)) {
        onMonthlyPriceSave(newCents);
      }
    }, 800);
  }, [product.effective_monthly_price_cents, onMonthlyPriceSave]);

  const handleMonthlyPriceChange = (e) => {
    const value = e.target.value;
    setMonthlyPriceDisplay(value);
    debouncedMonthlyPriceSave(value);
  };

  const revealsPath = promoCode ? `${revealsBasePath}/${promoCode.id}/ticket_reveals` : null;

  const productId = membershipProductId(product);

  const updatePromoState = useCallback((newPromoCode) => {
    onProductsChange((prev) => prev.map((p) => {
      if (membershipProductId(p) !== productId) return p;
      return { ...p, membership_promo_code: newPromoCode };
    }));
  }, [productId, onProductsChange, membershipProductId]);

  const createPromoCode = useCallback((attrs) => {
    return axios.post(promoCodePath, { membership_promo_code: attrs })
      .then(({ data }) => updatePromoState(data))
      .catch(() => toastError('Failed to save promo code'));
  }, [promoCodePath, updatePromoState]);

  const updatePromoCode = useCallback((attrs) => {
    return axios.put(promoCodePath, { membership_promo_code: attrs })
      .then(({ data }) => updatePromoState(data))
      .catch(() => toastError('Failed to save promo code'));
  }, [promoCodePath, updatePromoState]);

  const debouncedSavePromoCode = useCallback((attrs) => {
    clearTimeout(promoDebounceTimer.current);
    promoDebounceTimer.current = setTimeout(() => updatePromoCode(attrs), 800);
  }, [updatePromoCode]);

  const handlePromoEnable = () => {
    createPromoCode({
      percentage: 0,
      applies_to_ticket_types: false,
      applies_to_add_ons: false,
    });
  };

  const handlePromoDisable = () => {
    if (!promoCode?.id) return;
    axios.delete(promoCodePath)
      .then(() => updatePromoState(null))
      .catch(() => toastError('Failed to remove promo code'));
  };

  const getTicketReveals = () => product.promo_code_ticket_reveals || [];
  const selectedTicketTypeIds = getTicketReveals().map((r) => r.ticket_type_id);

  const getAddOnReveals = () => product.promo_code_add_on_reveals || [];
  const selectedAddOnIds = getAddOnReveals().map((r) => r.add_on_id);

  const ticketTypeOptions = (ticketTypes || [])
    .map((tt) => ({ value: tt.id, label: tt.name, disabled: selectedTicketTypeIds.includes(tt.id) }));

  const addOnOptions = (addOns || [])
    .map((ao) => ({ value: ao.id, label: ao.name, disabled: selectedAddOnIds.includes(ao.id) }));

  const createTicketReveal = (ticketTypeId) => withPendingReveal(`ticket-type-${ticketTypeId}`, () => (
    axios.post(revealsPath, {
      ticket_type_id: ticketTypeId,
    }).then(({ data }) => {
      onProductsChange((prev) => prev.map((p) => {
        if (membershipProductId(p) !== productId) return p;
        return {
          ...p,
          promo_code_ticket_reveals: [...(p.promo_code_ticket_reveals || []), data],
        };
      }));
    }).catch(() => toastError('Failed to add ticket type'))
  ));

  const destroyTicketReveal = (reveal) => withPendingReveal(`reveal-${reveal.id}`, () => (
    axios.delete(`${revealsPath}/${reveal.id}`)
      .then(() => {
        onProductsChange((prev) => prev.map((p) => {
          if (membershipProductId(p) !== productId) return p;
          return {
            ...p,
            promo_code_ticket_reveals: (p.promo_code_ticket_reveals || []).filter((r) => r.id !== reveal.id),
          };
        }));
      })
      .catch(() => toastError('Failed to remove ticket type'))
  ));

  const handleTicketTypeChange = (selectedIds) => {
    const currentReveals = getTicketReveals();
    const currentIds = currentReveals.map((r) => r.ticket_type_id);
    const newSelectedIds = selectedIds || [];

    newSelectedIds.filter((id) => !currentIds.includes(id)).forEach(createTicketReveal);
    currentReveals.filter((r) => !newSelectedIds.includes(r.ticket_type_id)).forEach(destroyTicketReveal);
  };

  const createAddOnReveal = (addOnId) => withPendingReveal(`add-on-${addOnId}`, () => (
    axios.post(revealsPath, {
      add_on_id: addOnId,
    }).then(({ data }) => {
      onProductsChange((prev) => prev.map((p) => {
        if (membershipProductId(p) !== productId) return p;
        return {
          ...p,
          promo_code_add_on_reveals: [...(p.promo_code_add_on_reveals || []), data],
        };
      }));
    }).catch(() => toastError('Failed to add add-on'))
  ));

  const destroyAddOnReveal = (reveal) => withPendingReveal(`reveal-${reveal.id}`, () => (
    axios.delete(`${revealsPath}/${reveal.id}`)
      .then(() => {
        onProductsChange((prev) => prev.map((p) => {
          if (membershipProductId(p) !== productId) return p;
          return {
            ...p,
            promo_code_add_on_reveals: (p.promo_code_add_on_reveals || []).filter((r) => r.id !== reveal.id),
          };
        }));
      })
      .catch(() => toastError('Failed to remove add-on'))
  ));

  const handleAddOnChange = (selectedIds) => {
    const currentReveals = getAddOnReveals();
    const currentIds = currentReveals.map((r) => r.add_on_id);
    const newSelectedIds = selectedIds || [];

    newSelectedIds.filter((id) => !currentIds.includes(id)).forEach(createAddOnReveal);
    currentReveals.filter((r) => !newSelectedIds.includes(r.add_on_id)).forEach(destroyAddOnReveal);
  };

  const getHoldReveals = () => product.promo_code_hold_reveals || [];
  const selectedHoldIds = getHoldReveals().map((r) => r.seating_event_hold_id);

  const setHoldReveals = (update) => onProductsChange((prev) => prev.map((p) => {
    if (membershipProductId(p) !== productId) return p;
    return { ...p, promo_code_hold_reveals: update(p.promo_code_hold_reveals || []) };
  }));

  const handleHoldsChange = (selectedIds) => {
    const newSelectedIds = selectedIds || [];

    newSelectedIds.filter((id) => !selectedHoldIds.includes(id)).forEach((holdId) => withPendingReveal(`hold-${holdId}`, () => (
      axios.post(revealsPath, { seating_event_hold_id: holdId })
        .then(({ data }) => setHoldReveals((reveals) => [...reveals, { id: data.id, seating_event_hold_id: holdId }]))
        .catch(() => toastError('Failed to add hold'))
    )));
    getHoldReveals().filter((r) => !newSelectedIds.includes(r.seating_event_hold_id)).forEach((reveal) => withPendingReveal(`reveal-${reveal.id}`, () => (
      axios.delete(`${revealsPath}/${reveal.id}`)
        .then(() => setHoldReveals((reveals) => reveals.filter((r) => r.id !== reveal.id)))
        .catch(() => toastError('Failed to remove hold'))
    )));
    (product.released_hold_reveal_ids || []).forEach((revealId) => withPendingReveal(`reveal-${revealId}`, () => (
      axios.delete(`${revealsPath}/${revealId}`)
        .then(() => onProductsChange((prev) => prev.map((p) => (
          membershipProductId(p) === productId
            ? { ...p, released_hold_reveal_ids: (p.released_hold_reveal_ids || []).filter((id) => id !== revealId) }
            : p
        ))))
        .catch(() => toastError('Failed to remove hold'))
    )));
  };

  const isPercentage = promoEnabled && promoCode.percentage != null && promoCode.percentage !== '';
  const isAmount = promoEnabled && promoCode.amount != null && promoCode.amount !== '';

  const stepLimitPerMember = (delta) => {
    const next = Math.max(0, (parseInt(limitPerMemberValue, 10) || 0) + delta);
    setLimitPerMemberValue(next);
    debouncedSavePromoCode({ ticket_limit_per_member: next });
  };

  const benefitSummary = () => (promoEnabled ? membershipBenefitSummary({
    name: membershipProduct.name,
    discountType: isPercentage ? 'percentage' : 'amount',
    percentage: percentageValue,
    amount: amountValue,
    appliesToTickets: promoCode.applies_to_ticket_types,
    ticketNames: ticketTypeOptions.filter((opt) => selectedTicketTypeIds.includes(opt.value)).map((opt) => opt.label),
    holdNames: (product.hold_options || []).filter((opt) => selectedHoldIds.includes(opt.value)).map((opt) => opt.label),
    holdsAllReleased: (product.released_hold_reveal_ids || []).length > 0,
    appliesToAddOns: promoCode.applies_to_add_ons,
    addOnNames: addOnOptions.filter((opt) => selectedAddOnIds.includes(opt.value)).map((opt) => opt.label),
    limit: limitPerMemberValue,
    household: membershipProduct.household,
  }) : null);

  return (
    <MembershipProductCardView
      idKey={membershipProduct.id}
      name={membershipProduct.name}
      editUrl={`/teams/${team.id}/membership_products/${membershipProduct.id}/edit`}
      priceLabel={product.base_price_to_currency}
      description={membershipProduct.description}
      imageUrl={membershipProduct.image_thumb_url}
      sellChecked={checked}
      onSellToggle={onToggle}
      showAnnual={hasAnnualPricing}
      annualPrice={priceDisplay}
      onAnnualPriceChange={handlePriceChange}
      showMonthly={hasMonthlyPricing}
      monthlyPrice={monthlyPriceDisplay}
      onMonthlyPriceChange={handleMonthlyPriceChange}
      priceHint="Modifying this price will only apply to memberships purchased for this show."
      waivesOrganizerFees={membershipProduct.waives_organizer_fees}
      organizerFeesWaived={product.waive_organizer_fees === true}
      onOrganizerFeesWaivedChange={onOrganizerFeesWaivedChange}
      benefitsEnabled={promoEnabled}
      onBenefitsToggle={() => promoEnabled ? handlePromoDisable() : handlePromoEnable()}
      discountType={isPercentage ? 'percentage' : 'amount'}
      onDiscountTypeChange={(value) => {
        if (value === 'percentage') {
          updatePromoCode({ percentage: 0, amount: null });
        } else {
          updatePromoCode({ amount: '0.00', percentage: null });
        }
      }}
      percentage={percentageValue}
      onPercentageChange={(e) => {
        const clamped = Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0));
        setPercentageValue(e.target.value === '' ? '' : clamped);
        debouncedSavePromoCode({ percentage: clamped });
      }}
      amount={amountValue}
      onAmountChange={(e) => {
        const raw = e.target.value.replace(/[^0-9.]/g, '');
        setAmountValue(e.target.value);
        debouncedSavePromoCode({ amount: raw });
      }}
      limit={limitPerMemberValue}
      onLimitChange={(e) => {
        const clamped = Math.max(0, parseInt(e.target.value, 10) || 0);
        setLimitPerMemberValue(e.target.value === '' ? '' : clamped);
        debouncedSavePromoCode({ ticket_limit_per_member: clamped });
      }}
      onLimitStep={stepLimitPerMember}
      household={membershipProduct.household}
      appliesToTickets={promoEnabled && promoCode.applies_to_ticket_types === true}
      onAppliesToTicketsChange={(isChecked) => {
        if (!isChecked) {
          [...getTicketReveals(), ...getHoldReveals()].forEach((reveal) => {
            axios.delete(`${revealsPath}/${reveal.id}`).catch(() => toastError('Failed to remove reveal'));
          });
          onProductsChange((prev) => prev.map((p) => {
            if (membershipProductId(p) !== productId) return p;
            return { ...p, promo_code_ticket_reveals: [], promo_code_hold_reveals: [] };
          }));
        }
        updatePromoCode({ applies_to_ticket_types: isChecked });
      }}
      ticketOptions={ticketTypeOptions}
      selectedTicketIds={selectedTicketTypeIds}
      onTicketsChange={handleTicketTypeChange}
      holdOptions={product.hold_options || []}
      selectedHoldIds={selectedHoldIds}
      onHoldsChange={handleHoldsChange}
      appliesToAddOns={promoEnabled && promoCode.applies_to_add_ons === true}
      onAppliesToAddOnsChange={(isChecked) => {
        if (!isChecked) {
          getAddOnReveals().forEach((reveal) => {
            axios.delete(`${revealsPath}/${reveal.id}`).catch(() => toastError('Failed to remove reveal'));
          });
          onProductsChange((prev) => prev.map((p) => {
            if (membershipProductId(p) !== productId) return p;
            return { ...p, promo_code_add_on_reveals: [] };
          }));
        }
        updatePromoCode({ applies_to_add_ons: isChecked });
      }}
      addOnOptions={addOnOptions}
      selectedAddOnIds={selectedAddOnIds}
      onAddOnsChange={handleAddOnChange}
      summary={benefitSummary()}
    />
  );
};

MembershipProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array,
  addOns: PropTypes.array,
  revealsBasePath: PropTypes.string.isRequired,
  promoCodePath: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  onPriceSave: PropTypes.func.isRequired,
  onMonthlyPriceSave: PropTypes.func.isRequired,
  onOrganizerFeesWaivedChange: PropTypes.func.isRequired,
  onProductsChange: PropTypes.func.isRequired,
  membershipProductId: PropTypes.func.isRequired,
};

const MembershipsTab = ({
  csrfToken,
  team,
  confirm,
  ticketTypes,
  addOns,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const basePath = `/teams/${team.id}/confirms/${confirm.id}/confirm_membership_products`;
  const revealsBasePath = `/teams/${team.id}/confirms/${confirm.id}/promo_codes`;

  const saveProduct = (product, data) => {
    if (product.override_id) {
      return axios.put(`${basePath}/${product.override_id}`, data);
    }
    return axios.post(basePath, { ...data, membership_product_id: product.membership_product.id });
  };

  useEffect(() => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.get(basePath)
      .then(({ data }) => {
        setProducts(data);
      })
      .catch(() => {
        toastError('Failed to load membership products');
      })
      .finally(() => setLoading(false));
  }, []);

  const membershipProductId = (product) => product.membership_product.id;

  const handleProductUpdated = (updatedProduct) => {
    setProducts((prev) =>
      prev.map((p) => (membershipProductId(p) === membershipProductId(updatedProduct) ? updatedProduct : p))
    );
  };

  const handleToggle = (product) => {
    const newEnabled = !product.checked;

    if (newEnabled && !product.effective_price_cents && !product.effective_monthly_price_cents) {
      toastError('Set a price before enabling this membership.');
      return;
    }

    saveProduct(product, { enabled: newEnabled })
      .then(({ data }) => handleProductUpdated(data))
      .catch(() => toastError('Failed to save'));
  };

  const handleOrganizerFeesWaivedChange = (product, checked) => {
    saveProduct(product, { waive_organizer_fees: checked, enabled: product.checked })
      .then(({ data }) => handleProductUpdated(data))
      .catch(() => toastError('Failed to save'));
  };

  const handlePriceSave = (product, priceCents) => {
    saveProduct(product, { price_cents: priceCents, enabled: true })
      .then(({ data }) => {
        handleProductUpdated(data);
        toastSuccess('Price saved');
      })
      .catch(() => toastError('Failed to save price'));
  };

  const handleMonthlyPriceSave = (product, priceCents) => {
    saveProduct(product, { monthly_price_cents: priceCents, enabled: true })
      .then(({ data }) => {
        handleProductUpdated(data);
        toastSuccess('Monthly price saved');
      })
      .catch(() => toastError('Failed to save monthly price'));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-4">
        <Spinner aria-label="Loading memberships" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div style={{ fontSize: '14px' }}>
        <div className="row mt-3">
          <div className="col-12">
            <h3 style={{ fontSize: '18px' }}>
              <strong>Memberships</strong>
            </h3>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-12">
            <p style={mutedTextStyle}>
              No membership products are available for this venue. Create membership products in your team settings to offer them during checkout.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontSize: '14px' }}>
      <div className="row mt-3">
        <div className="col-12">
          <Card>
              <h3 style={{ fontSize: '18px' }} className="mb-3">
                <strong>Memberships</strong>
              </h3>
              <p className="small" style={mutedTextStyle}>
                Configure membership checkout and discount settings for this event.
              </p>
              {products.map((product) => (
                <MembershipProductCard
                  key={membershipProductId(product)}
                  product={product}
                  team={team}
                  ticketTypes={ticketTypes}
                  addOns={addOns}
                  revealsBasePath={revealsBasePath}
                  promoCodePath={`/teams/${team.id}/confirms/${confirm.id}/membership_products/${membershipProductId(product)}/membership_promo_code`}
                  onToggle={() => handleToggle(product)}
                  onPriceSave={(priceCents) => handlePriceSave(product, priceCents)}
                  onMonthlyPriceSave={(priceCents) => handleMonthlyPriceSave(product, priceCents)}
                  onOrganizerFeesWaivedChange={(checked) => handleOrganizerFeesWaivedChange(product, checked)}
                  onProductsChange={setProducts}
                  membershipProductId={membershipProductId}
                />
              ))}
          </Card>
        </div>
      </div>
    </div>
  );
};

MembershipsTab.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array,
  addOns: PropTypes.array,
};

export default MembershipsTab;
